<template>
	<div id="questions-bank">
		<v-row no-gutters class="fill-height">
			<!-- sidebar -->
			<v-col lg="2" sm="4" cols="12" class="pa-3">
				<div class="font-weight-bold text-h6">معلومات بنك الأسئلة</div>

				<!-- add new question -->
				<v-btn
					:to="{
						name: 'question',
						query: {
							classId: $route.query.classId,
							subjectId: $route.query.subjectId
						}
					}"
					depressed
					width="100%"
					color="primary"
					class="mt-3"
				>
					<v-icon class="me-2">mdi-plus</v-icon>
					إضافة أسئلة جديدة
				</v-btn>

				<div class="mt-4">
					<!-- class -->
					<v-autocomplete
						v-model="classId"
						:items="classes"
						item-text="name"
						item-value="id"
						label="الصف"
						outlined
						dense
						hide-details
						class="mb-3"
						@change="
							!$event ? subjectId = null : null;
							$router.replace($event ? { query: { ...$route.query, classId } } : {});
							fetchSubjects(classId)

						"
					/>

					<!-- tree -->
					<v-card
						flat
						max-width="100%"
						:height="$vuetify.breakpoint.height - (!isAddingToExam ? 210 : 260)"
						class="overflow-x-hidden"
					>
						<v-progress-linear v-if="loadingSubjectsTree" indeterminate/>
						<bee-treeview
							v-else
							:items="subjectsTree"
							item-children="subjectChilds"
							expand-icon="mdi-chevron-down"
							:disabled="!Boolean($route.query.classId) || loadingQuestions"
							root-class="primary--text"
							item-class="text-body-2"
							active-class="purple--text"
							dense
							activatable
							hoverable
							:active.sync="openNode"
							:open-item.sync="subjectId"
							@update:active="
								subjectId = $event[0]
								$router.replace({ query: { ...$route.query, subjectId } });
							"
						/>
					</v-card>
				</div>
			</v-col>
			<v-divider v-if="$vuetify.breakpoint.smAndUp" vertical/>

			<!-- content -->
			<v-col lg="10" sm="8" cols="12">
				<!-- breadcrumbs -->
				<div
					class="d-flex flex-wrap flex-sm-row flex-column-reverse justify-space-between align-sm-center pa-3"
				>
					<!-- breadcrumbs -->
					<bee-breadcrumbs
						:items="breadcrumbsItems"
						class="mt-sm-0 mt-3"
					/>
				</div>

				<v-divider/>

				<div class="pa-3">
					<!-- questions -->
					<div v-if="($route.query.classId && selectedSubject) || isAddingToExam">
						<bee-handy-table
							ref="table"
							:headers="headers"
							:items="[]"
							:items-per-page="50"
							:loading="loadingQuestions"
							fixed-header
							:height="$vuetify.breakpoint.height - (!isAddingToExam ? 160 : 200)"
							:data-table-props="{ disableSort: true, showExpand: true, singleExpand: true }"
							dense
							zebra
							with-columns-divider
							pagination-on-scroll
							pagination-on-scroll-server-items
							:server-items-length="totalItemsLength"
							shown-rows-text
							hide-default-footer
							table-class="overflow-hidden"
							@pagination-on-scroll:reach-last-row="fetchQuestions"
						>
							<!-- select -->
							<template v-slot:item.select="{ item }">
								<v-checkbox
									:input-value="selectedQuestions.includes(item.id)"
									:ripple="false"
									hide-details
									color="success"
									class="flat-checkbox mt-0 pt-0"
									@change="
										!selectedQuestions.includes(item.id)
											? selectedQuestions.push(item.id)
											: selectedQuestions.splice(selectedQuestions.indexOf(item.id), 1)
									"
								/>
							</template>
							
							<!-- question -->
							<template v-slot:item.question="{ item }">
								<!-- unexpanded -->
								<div dir="ltr" class="d-flex align-center">
									<v-img
										v-if="item.questionImages.length && !item.rowDataExpand"
										:src="item.questionImages[0].imagePath"
										width="48"
										max-width="48"
										height="48"
										max-height="48"
										class="rounded ma-1"
									/>
									<p
										dir="auto"
										class="flex-grow-1 content-margin-0"
										:class="!item.rowDataExpand ? 'mb-0' : 'my-2'"
										v-html="item.name"
									></p>
								</div>

								<!-- expanded -->
								<div v-if="item.rowDataExpand" dir="auto" class="d-flex align-start mb-2">
									<div>
										<div
											v-for="(item, i) in item.choices"
											:key="item.id"
										>
											<p
												class="mb-2"
												:class="item.isCorrect ? 'success--text' : ''"
											>
												{{ (i + 1) + '. ' + item.choice }}
											</p>
										</div>
									</div>
									<v-img
										v-if="item.questionImages.length"
										:src="item.questionImages[0].imagePath"
										max-width="140"
										max-height="140"
										class="rounded mx-6"
									/>
								</div>
							</template>

							<!-- difficulty -->
							<template v-slot:item.difficulty="{ item }">
								{{questionDifficulties.find(c => c.id === item.difficulty)?.name || ''}}
							</template>

							<!-- actions -->
							<template v-slot:item.actions="{ item }">
								<!-- edit -->
								<v-btn
									:to="{
										name: 'question',
										params: { state: 'update-data' },
										query: {
											questionId: item.id,
											classId: $route.query.classId,
											subjectId: $route.query.subjectId
										}
									}"
									icon
									small
									color="primary"
								>
									<v-icon>mdi-pencil</v-icon>
								</v-btn>
							</template>
							
							<!-- data expand -->
							<template v-slot:item.data-table-expand="{ item, expand, isExpanded }">
								<!-- single -->
								<v-btn
									v-if="!item.childsQuestions.length"
									icon
									small
									@click="
										expand(!isExpanded)
										collapseSingleQuestions(item)
									"
								>
									<v-icon>mdi-chevron-down {{isExpanded ? 'mdi-rotate-180' : ''}}</v-icon>
								</v-btn>

								<!-- group -->
								<v-btn
									v-else
									icon
									small
									@click="
										collapseSingleQuestions();
										expand(!isExpanded)
									"
								>
									<v-icon>
										mdi-{{!item.rowDataExpand ? 'unfold-more-horizontal' : 'unfold-less-horizontal'}}
									</v-icon>
								</v-btn>
							</template>

							<!-- expand data for group -->
							<template v-slot:expanded-item="{ headers, item }">
								<td
									v-if="item.childsQuestions.length"
									:colspan="headers.length"
									class="px-2 py-1"
								>
									<div
										v-for="(subQuestion) in item.childsQuestions"
										:key="subQuestion.id"
										dir="auto"
									>
										<p
											v-if="subQuestion.name && subQuestion.name !== 'divider'"
											dir="auto"
											class="my-2"
										>
											<!-- {{(i + 1) + '. '}}  -->
											<span
												v-html="subQuestion.name"
												class="d-inline-block content-margin-0 text-pre-line"
											/>
										</p>
										<hr
											v-if="subQuestion.name && subQuestion.name === 'divider'"
											class="my-2"
										>
										<div class="d-flex align-start">
											<div v-if="subQuestion.choices.length">
												<div
													v-for="(option, j) in subQuestion.choices"
													:key="option.id"
												>
													<p
														v-if="option.choice"
														class="mb-2"
														:class="{ 'success--text': option.isCorrect }"
													>
														{{(j + 1) + '. ' + option.choice }}
													</p>
												</div>
											</div>
											<v-img
												v-if="subQuestion.questionImages.length"
												:src="subQuestion.questionImages[0].imagePath"
												max-width="100"
												max-height="100"
												class="rounded mx-6"
											/>
										</div>
									</div>
								</td>
							</template>
						</bee-handy-table>
					</div>

					<!-- please select -->
					<div
						v-else
						class="d-flex flex-column align-center mt-6"
					>
						<v-icon size="40" color="primary" class="mb-4">
							mdi-clipboard-text-multiple-outline
						</v-icon>
						<p class="mb-0 font-weight-medium">
							يرجى اختيار الصف ونوع الاختبار
						</p>
					</div>
				</div>
			</v-col>
		</v-row>

		<!-- footer -->
		<v-footer
			v-if="isAddingToExam"
			app
			color="white"
			class="justify-end"
			style="outline: 1px solid #e0e0e0;"
		>
			<v-btn
				depressed
				:disabled="addQuestionsLoading"
				:to="{ name: 'exam', params: { id: $route.query.examId } }"
			>
				عودة إلى الامتحان
			</v-btn>
			<v-btn
				depressed
				:disabled="!selectedQuestions.length"
				:loading="addQuestionsLoading"
				color="primary"
				class="ms-2"
				@click="addQuestionsToExam"
			>
				إدراج الأسئلة المحدد
			</v-btn>
		</v-footer>
	</div>
</template>

<script>
import { questionDifficulties, questionDifficulty, questionType } from '@/helpers/enums.js';
import { mapGetters, mapState } from 'vuex';
export default {
	name: 'QuestionsBank',

	data() {
		return {
			questionDifficulties,
			questionType,

			loadingSubjectsTree: false,
			loadingQuestions: false,
			addQuestionsLoading: false,

			classId: +this.$route.query.classId,
			subjectId: +this.$route.query.subjectId,
			openNode: [+this.$route.query.subjectId],

			totalItemsLength: 0,

			selectedQuestions: [],

			subjectsTree: [],
			subjectsTree0: [
				{
					id: '1',
					name: 'Reading',
					subjectChilds: [
						{ id: '1-1', name: 'Reading in phrases', value: 'readingInPhrases' },
						{ id: '1-2', name: 'Pace', value: 'pace' },
						{ id: '1-3', name: 'Syntax', value: 'syntax' },
						{ id: '1-4', name: 'Self-corrections', value: 'selfCorrections' },
						{ id: '1-5', name: 'Intonation', value: 'intonation' },
						{ id: '1-6', name: 'Letter naming fluency', value: 'letterNamingFluency' },
						{ id: '1-7', name: 'Slight word fluency', value: 'slightWordFluency' },
						{ id: '1-8', name: 'Comprehension', value: 'comprehension' },
					]
				},
				{
					id: '2',
					name: 'Phonics awareness',
					subjectChilds: [
						{ id: '2-1', name: 'Phoneme categorization', value: 'phonemeCategorization' },
						{ id: '2-2', name: 'Phoneme blending', value: 'phonemeBlending' },
						{ id: '2-3', name: 'Phoneme segmentation', value: 'phonemeSegmentation' },
						{ id: '2-4', name: 'Phoneme segmentation', value: 'phonemeSegmentation' },
					],
				},
				{
					id: '3',
					name: 'Readers to writers',
					subjectChilds: [
						{ id: '3-1', name: 'Spelling', value: 'spelling' },
						{ id: '3-2', name: 'Vocabulary', value: 'vocabulary' },
						{ id: '3-3', name: 'Ideas and content', value: 'ideasAndContent' },
						{ id: '3-4', name: 'Grammar', value: 'grammar' },
						{ id: '3-5', name: 'Structural analysis', value: 'structuralAnalysis' },
						{ id: '3-6', name: 'Writing', value: 'writing' },
					],
				},
				{
					id: '4',
					name: 'Listening and speaking',
					subjectChilds: [
						{ id: '4-1', name: 'Interactive read aloud', value: 'interactiveReadAloud' },
						{ id: '4-2', name: 'Retelling cards', value: 'retellingCards' },
					],
				},
				{
					id: '5',
					name: 'Social and study skills',
					subjectChilds: [
						{ id: '5-1', name: 'Participation', value: 'participation' },
						{ id: '5-2', name: 'Completion of work', value: 'completionOfWork' },
						{ id: '5-3', name: 'Concentration', value: 'concentration' },
						{ id: '5-4', name: 'Self direction', value: 'selfDirection' },
						{ id: '5-5', name: 'Cooperation with others', value: 'cooperationWithOthers' },
						{ id: '5-6', name: 'Behavior', value: 'behavior' },
						{ id: '5-7', name: 'Presentation of assignments', value: 'presentationOfAssignments' },
					],
				},
				{
					id: '7',
					name: 'Movement video',
					subjectChilds: [
						{ id: '7-1', name: 'Movement video', value: 'movementVideo' }
					]
				}
			],

			examQuestions: [],
		}
	},

	watch: {
		'$route.query.subjectId'(val) {
			if (val) { this.fetchQuestions(); }
		}
	},

	computed: {
		...mapState({
			classes: state => state.classes.classes,
			subSubjects: state => state.subjects.subSubjects,
		}),

		...mapGetters({
			getClassById: 'classes/getClassById',
			getSubSubjectById: 'subjects/getSubSubjectById',
		}),

		isAddingToExam() {
			return Boolean(this.$route.query.examId);
		},

		breadcrumbsItems() {
			const items = [];

			if (this.$route.query.classId) {
				items.push({
					to: null,
					disabled: true,
					text: this.getClassById(+this.$route.query.classId).name
				})
			}
			if (this.selectedSubject) {
				items.push({
					to: null,
					disabled: true,
					text: this.selectedSubject.parent
				}, {
					to: null,
					disabled: true,
					text: this.selectedSubject.name
				})
			}

			return [
				{
					to: null,
					disabled: false,
					icon: 'mdi-clipboard-text-multiple-outline',
					text: 'بنك الأسئلة'
				},
				...items
			]
		},

		subjects() {
			return this.subjectsTree.flatMap(c =>
				c.subjectChilds.map(e => ({ ...e, parent: c.name, parentId: c.id }))
			)
		},

		selectedSubject() {
			if (this.subjectId) {
				return this.getSubSubjectById(+this.subjectId);
			} else {
				return null;
			}
		},

		headers() {
			const headers = [
				{
					text: '#',
					name: 'no',
					value: 'no',
					width: 20,
					align: 'center',
					setValueExpr: (item, index) => index + 1,
				},
				this.isAddingToExam
					? {
						text: '',
						name: 'select',
						value: 'select',
						width: 40,
					}
					: null,
				{
					text: 'السؤال',
					name: 'question',
					value: 'question',
				},
				{
					text: 'درجة الصعوبة',
					name: 'difficulty',
					value: 'difficulty',
					width: 120,
				},
				{
					text: 'المدرس',
					name: 'teacher',
					value: 'teacher.fullName',
					width: 160,
				},
				{
					text: '',
					name: 'actions',
					value: 'actions',
					width: 30,
					columnClass: 'pa-1',
				},
				{
					text: '',
					name: 'data-table-expand',
					value: 'data-table-expand',
					width: 30,
					columnClass: 'pa-1',
					divider: false,
				},
			].filter(Boolean);

			for (let i = 0; i < headers.length; i++) {
				headers[i].columnClass =
					!['actions', 'data-table-expand'].includes(headers[i].name)
						? (headers[i].columnClass ? headers[i].columnClass + ' ' : '') + 'px-2'
						: headers[i].columnClass;
				headers[i].class = (headers[i].class ? headers[i].class + ' ' : '') + 'color-1-light';
			}

			return headers;
		},

		questions0() {
			let items = [];
			const generateQuestion = (type, id, lang) => {
				const question = lang === 'ar'
					? 'سيكون عنوان السؤال الرئيسي هنا ، عنوان السؤال الرئيسي سيكون صحيحًا __________.'
					: 'The Main Question Title Will Be Right Here, The Main Question Title Will Be Right _________________ .';
				const difficulty =
					[questionDifficulty.easy, questionDifficulty.medium, questionDifficulty.hard][Math.floor(Math.random() * 3)]
				const option = lang === 'ar' ? 'الاختيار' : 'Option';
				const teacher = ['أحمد علي', 'سارة محمد', 'خالد يوسف'][Math.floor(Math.random() * 3)];
				const image =
					Math.random() > 0.5 ? 'https://plus.unsplash.com/premium_photo-1664474619075-644dd191935f' : null;
				const correctAnswer = [0, 1, 2, 3][Math.floor(Math.random() * 4)];
				return type === questionType.single
					? {
						id,
						type: questionType.single,
						question,
						difficulty,
						image,
						options: [
							{ id: 'a', text: `${option} a`, isCorrect: correctAnswer === 0 },
							{ id: 'b', text: `${option} b`, isCorrect: correctAnswer === 1 },
							{ id: 'c', text: `${option} c`, isCorrect: correctAnswer === 2 },
							{ id: 'd', text: `${option} d`, isCorrect: correctAnswer === 3 },
						],
						teacher,
						subQuestions: [],
					}
					: {
						id,
						type: questionType.group,
						question,
						difficulty,
						image,
						options: [],
						teacher,
						subQuestions: Array.from({ length: 3 }).map((c, i) => generateQuestion(questionType.single, i, lang)),
					}
			}

			items.push(
				...Array.from({ length: Math.round(Math.random() * 100) })
					.map((c, i) =>
						generateQuestion(
							[questionType.single, questionType.group][Math.floor(Math.random() * 2)],
							i + 1,
							['ar', 'en'][Math.floor(Math.random() * 2)]
						)
					)

			);
			
			items = items.map(c => c.type === questionType.single ? { ...c, rowDataExpand: false } : c);
			return items;
		},

		allowedSubjectsForExam() {
			if (this.$route.query.examSubjects) {
				return this.$route.query.examSubjects.map(Number);
			} else {
				return [];
			}
		}
	},

	methods: {
		fetchSubjects(classId) {
			if (!classId) return new Promise(resolve => resolve());
			this.loadingSubjectsTree = true;
			return this.$store.dispatch('subjects/fetchByClassId', { classId }).then(data => {
				this.subjectsTree = data.map(c => ({
					...c,
					subjectChilds: c.subjectChilds.map(child => ({
						...child,
						disabled: this.allowedSubjectsForExam.length
							? !this.allowedSubjectsForExam.includes(child.id)
							: false,
					}))
				}));
			}).finally(() => {
				this.loadingSubjectsTree = false;
			})
		},

		fetchQuestions(newScrollOptions, isFirstLoad = false) {
			if (this.selectedSubject || this.isAddingToExam) {
				let page = 1;
				if (newScrollOptions) {
					page = newScrollOptions.page
				} else if (this.$refs.table) {
					this.$refs.table.resetShownRows();
				}
	
				this.loadingQuestions = true;
				return this.$store.dispatch('questions/fetchAll', {
					childSubjectId: this.selectedSubject ? this.selectedSubject.id : null,
					parentSubjectId: this.isAddingToExam ? this.$route.query.subjectId : null,
					page,
					perPage: 50,
				}).then(({ data, total }) => {
					this.$refs.table.addScrollItems(data.map(c => ({ ...c, rowDataExpand: false })));
					this.totalItemsLength = total;
				}).finally(() => {
					if (!isFirstLoad) this.loadingQuestions = false;
				})
			}
		},

		collapseSingleQuestions(item = {}) {
			const targetItem =
				this.$refs.table.paginationOnScrollData.serverItems.find(c => c.id === item.id);
			const expandedItem =
				this.$refs.table.paginationOnScrollData.serverItems.find(c => c.rowDataExpand);
				
			if (targetItem) { targetItem.rowDataExpand = !targetItem.rowDataExpand; }
			if (expandedItem) { expandedItem.rowDataExpand = false; }
		},

		async addQuestionsToExam() {
			if (!this.selectedQuestions.length) return;
			this.addQuestionsLoading = true;
			try {
				await this.$store.dispatch('exams/updateQuestions', {
					testId: this.$route.query.examId,
					questions: [
						...this.examQuestions.map(c => ({
							questionId: c.questionsBankId,
							degree: c.degree,
						})),
						...this.selectedQuestions.map(id => ({
							questionId: id,
							degree: 0,
						}))
					].filter((c, i, arr) => arr.findIndex(e => e.questionId === c.questionId) === i),
				})
				this.$snackbar.info('تم إضافة الأسئلة إلى الامتحان');
				this.selectedQuestions = [];
				this.$router.push({
					name: 'exam',
					params: { id: this.$route.query.examId }
				});
			} catch (e) {
				console.error(e);
				this.loadingQuestions = false;
			} finally {
				this.loadingQuestions = false;
			}
		},
	},

	created() {
		if (this.$route.query.classId) {
			this.fetchSubjects(this.$route.query.classId).finally(() => {
				Promise.all([
					this.fetchQuestions(undefined, true),
					this.$route.query.examId
						? this.$store.dispatch('exams/fetchQuestions', {
							id: this.$route.query.examId
						}).then(data => {
							this.examQuestions = data;
							this.selectedQuestions = data.map(c => c.questionsBankId);
						})
						: null
				]).finally(() => {
					this.loadingQuestions = false;
				})
			});
		}
	}
}
</script>

<style lang="scss">
#questions-bank {
	height: 100%;

	.v-data-table__expanded__content {
		box-shadow: unset;
	}
}
</style>